import { InlineMessage } from '@strata/react-inline-message';

import { Columns } from 'components/shared/Columns';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const clear = [
    <Columns
        key="1"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <div className="sds_section__principles-description">
                        Healthcare is a complex domain full of confusing jargon, inconsistencies,
                        and acronyms. Let&#39;s not add to the problem. Tame complexity through
                        plain language and intuitive design patterns and interactions.
                    </div>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Questions to help you apply this principle"
                        label={
                            <ul>
                                <li>
                                    Have we eliminated unnecessary acronyms, jargon, and
                                    inconsistent language?
                                </li>
                                <li>
                                    Have we created an efficient process by eliminating unnecessary
                                    steps or interactions from the user?
                                </li>
                            </ul>
                        }
                    />
                </div>
                <div className="sds_image__principle-illustration-container">
                    <img
                        className="sds_image__principle-illustration"
                        src="https://sseasacdn.blob.core.windows.net/strata-website/Principles_Clear.svg"
                        alt="Illustration for the Strata Design System principle clear"
                        loading="lazy"
                    />
                </div>
            </>
        }
    />,
];

const cohesive = [
    <Columns
        key="2"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <div className="sds_section__principles-description">
                        Design Systems are the framework for cohesive applications. Streamline
                        workflows to make great user experiences, and be rigorous in evaluating
                        potential new components, prioritizing reusability across our applications.
                    </div>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Questions to help you apply this principle"
                        label={
                            <ul>
                                <li>
                                    Are we using consistent language and terms for this persona?
                                </li>
                                <li>Are we reusing components and patterns where possible?</li>
                            </ul>
                        }
                    />
                </div>
                <div className="sds_image__principle-illustration-container">
                    <img
                        className="sds_image__principle-illustration"
                        src="https://sseasacdn.blob.core.windows.net/strata-website/Principles_Cohesive.svg"
                        alt="Illustration for the Strata Design System principle cohesive"
                        loading="lazy"
                    />
                </div>
            </>
        }
    />,
];

const energetic = [
    <Columns
        key="1"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <div className="sds_section__principles-description">
                        There is an energy to the mission we are pursuing within our space and we
                        want users to feel that energy within our products. Also our design system
                        is a living system, always ready for change and the next challenge.
                    </div>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Questions to help you apply this principle"
                        label={
                            <ul>
                                <li>
                                    Where possible, does this experience further our company
                                    mission?
                                </li>
                                <li>
                                    Can we imagine or perceive an improved experience that we are
                                    unwilling to provide because of an established pattern or
                                    process?
                                </li>
                            </ul>
                        }
                    />
                </div>
                <div className="sds_image__principle-illustration-container">
                    <img
                        className="sds_image__principle-illustration"
                        src="https://sseasacdn.blob.core.windows.net/strata-website/Principles_Energetic.svg"
                        alt="Illustration for the Strata Design System principle energetic"
                        loading="lazy"
                    />
                </div>
            </>
        }
    />,
];

const exemplary = [
    <Columns
        key="1"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <div className="sds_section__principles-description">
                        We strive for excellence in everything that we do. Everything should be
                        approached with holistic, thoughtful, and well-crafted design solutions.
                        Every detail matters.
                    </div>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Questions to help you apply this principle"
                        label={
                            <ul>
                                <li>
                                    What persona pain points or goals are we solving with this
                                    experience?
                                </li>
                                <li>
                                    Have we considered the holistic problem we are solving? If not,
                                    what part of the problem are we solving and what is the plan for
                                    approaching the rest of the problem in the future?
                                </li>
                            </ul>
                        }
                    />
                </div>
                <div className="sds_image__principle-illustration-container">
                    <img
                        className="sds_image__principle-illustration"
                        src="https://sseasacdn.blob.core.windows.net/strata-website/Principles_Exemplary.svg"
                        alt="Illustration for the Strata Design System principle exemplary"
                        loading="lazy"
                    />
                </div>
            </>
        }
    />,
];

const trust = [
    <Columns
        key="1"
        columns={Columns.ColumnNumber.TWO}
        content={
            <>
                <div>
                    <div className="sds_section__principles-description">
                        We build trust into every interaction and maintain trust by being
                        thoughtful, reliable, and honest in our intentions. We enable an unbiased,
                        transparent process by instilling trust between payers, providers and
                        patients.
                    </div>
                    <InlineMessage
                        wrapText
                        intent={InlineMessage.Intent.INFO}
                        title="Questions to help you apply this principle"
                        label={
                            <ul>
                                <li>
                                    Are we unintentionally hiding or withholding valuable
                                    information from the user?
                                </li>
                            </ul>
                        }
                    />
                </div>
                <div className="sds_image__principle-illustration-container">
                    <img
                        className="sds_image__principle-illustration"
                        src="https://sseasacdn.blob.core.windows.net/strata-website/Principles_Trust.svg"
                        alt="Illustration for the Strata Design System principle trust"
                        loading="lazy"
                    />
                </div>
            </>
        }
    />,
];

export const StrataDesignPrinciples = () => (
    <div>
        <Page
            pageTitle="Strata Design Principles"
            pageDescription="Our design principles keep important values front and center in the design process and ensure consistency in decision making across our team."
            pageContent={
                <>
                    <Section title="#1 Clear" contentArray={clear} />
                    <Section title="#2 Cohesive" contentArray={cohesive} />
                    <Section title="#3 Energetic" contentArray={energetic} />
                    <Section title="#4 Exemplary" contentArray={exemplary} />
                    <Section title="#5 Trust" contentArray={trust} />
                </>
            }
        />
    </div>
);
